import React from 'react';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import useMicLevel from './useMicLevel';
import { meterDisplayState, METER_SEGMENTS } from './micMeterLogic';

// Ambient, not a feature. A thin strip you notice only when it stops moving.
//
// A two-column grid rather than stacked flex rows: the caption belongs in
// column 2 so it starts where the bar starts. As a full-width row underneath it
// ran back under the icon, which put "No sound from your mic" hard against the
// mic glyph with nothing between them.
const Wrapper = styled.div`
    width: 100%;
    box-sizing: border-box;
    display: grid;
    /* minmax(0, 1fr), not 1fr: a 1fr track floors at min-content, and with the
       caption set to nowrap that floor is the whole string, so the track grew
       past the dock instead of letting text-overflow ellipsise. */
    grid-template-columns: auto minmax(0, 1fr);
    column-gap: 6px;
    row-gap: 5px;
    align-items: center;

    i.icon {
        margin: 0 !important;
        font-size: 0.75rem;
        line-height: 1;
        height: 1em;
        // The icon font leaves slack beneath the glyph, so aligning on the
        // inline baseline sat the bars low against the mic. Centring the glyph
        // inside its own box takes the font metrics out of it and lines the
        // two up on the same axis.
        display: flex;
        align-items: center;
        justify-content: center;
        color: rgba(255, 255, 255, 0.45);
        flex-shrink: 0;
    }
`;

const Segments = styled.div`
    display: flex;
    gap: 2px;
    min-width: 0;
`;

const Segment = styled.div.withConfig({
    shouldForwardProp: (prop) => !['lit', 'tone'].includes(prop),
})`
    flex: 1;
    min-width: 0;
    height: 4px;
    border-radius: 1px;
    // Unlit segments stay faintly visible so a dead mic reads as an empty meter
    // rather than as no meter at all.
    background: ${(props) => {
        if (!props.lit) return 'rgba(255, 255, 255, 0.12)';
        if (props.tone === 'hot') return '#ef4444';
        if (props.tone === 'warm') return '#d9a441';
        return '#3f9d78';
    }};
    transition: background 80ms linear;
`;

// Holds one line whether or not a message is showing, so nothing below shifts
// when the warning appears. Both messages fit the dock on one line; if a longer
// one is ever added it ellipsises rather than pushing the dock open.
const CaptionSlot = styled.div`
    /* Column 2 keeps the caption lined up with the bar rather than running
       back underneath the mic glyph. */
    grid-column: 2;
    min-width: 0;
    min-height: 14px;
`;

const Caption = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'tone',
})`
    font-size: 0.65rem;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(props) => (props.tone === 'warning' ? '#d9a441' : 'rgba(255, 255, 255, 0.4)')};
`;

// The top of the bar runs amber then red, so a healthy speaking level reads as
// green with occasional amber rather than pinning the meter.
const toneFor = (index) => {
    if (index >= METER_SEGMENTS - 2) return 'hot';
    if (index >= METER_SEGMENTS - 4) return 'warm';
    return 'cool';
};

/**
 * Live microphone level for the recording controls.
 *
 * Owns the level hook so that sampling (several times a second) re-renders only
 * this subtree, not the whole recording screen. Memoized for the same reason:
 * the parent re-renders on every interaction timer tick and store update.
 *
 * Reports only. It never blocks or stops a recording.
 */
const MicLevelMeter = ({
    microphoneStream,
    isMicrophoneActive = true,
    recording = false,
    // Optional active-speaker hookup for the tile layout: a ref to a DOM node
    // that gets a `speaking` class toggled while the mic reads voice-level
    // input. Done as a direct class toggle — never React state in a parent —
    // so the ~8Hz level sampling keeps re-rendering ONLY this subtree, and no
    // second audio pipeline is created (this component's single useMicLevel
    // stays the one MediaStreamSource on the stream; a second one is silent
    // in Chrome).
    speakingTargetRef = null
}) => {
    const { segments, quietMs, available } = useMicLevel(microphoneStream);
    const speakingHoldRef = React.useRef(null);

    const mode = meterDisplayState({
        available,
        isMicrophoneActive,
        isRecording: recording,
        quietMs
    });

    // ≥3 of 12 segments ≈ real voice rather than room noise (thresholds pinned
    // by micMeterLogic.test.js). Hold the class briefly after the last loud
    // sample so natural speech pauses don't strobe the ring.
    const speakingNow = mode !== 'muted' && mode !== 'hidden' && segments >= 3;
    React.useEffect(() => {
        const el = speakingTargetRef?.current;
        if (!el) return undefined;
        if (speakingNow) {
            el.classList.add('speaking');
            if (speakingHoldRef.current) clearTimeout(speakingHoldRef.current);
            speakingHoldRef.current = null;
        } else if (el.classList.contains('speaking') && !speakingHoldRef.current) {
            speakingHoldRef.current = setTimeout(() => {
                el.classList.remove('speaking');
                speakingHoldRef.current = null;
            }, 450);
        }
        return undefined;
    }, [speakingNow, speakingTargetRef]);
    React.useEffect(() => () => {
        if (speakingHoldRef.current) clearTimeout(speakingHoldRef.current);
        speakingTargetRef?.current?.classList.remove('speaking');
    }, [speakingTargetRef]);

    if (mode === 'hidden') {
        return null;
    }

    const litCount = mode === 'muted'
        ? 0
        : Math.max(0, Math.min(METER_SEGMENTS, segments));

    return (
        <Wrapper>
            <Icon name={mode === 'muted' ? 'microphone slash' : 'microphone'} />
            <Segments
                role="meter"
                aria-label="Microphone input level"
                aria-valuenow={litCount}
                aria-valuemin={0}
                aria-valuemax={METER_SEGMENTS}
                aria-valuetext={`${litCount} of ${METER_SEGMENTS}`}
            >
                {Array.from({ length: METER_SEGMENTS }, (_, index) => (
                    <Segment key={index} lit={index < litCount} tone={toneFor(index)} />
                ))}
            </Segments>

            <CaptionSlot>
                {mode === 'muted' && <Caption>Muted</Caption>}
                {mode === 'warning' && (
                    // role="alert" so a screen reader announces it. Without this the
                    // users least able to notice a dead level bar get nothing.
                    <Caption tone="warning" role="alert">
                        No sound from your mic
                    </Caption>
                )}
            </CaptionSlot>
        </Wrapper>
    );
};

export default React.memo(MicLevelMeter);
